import { PAYTABLE_SECTIONS }    from '../../constants/paytableSections/paytableSections.constant'
import { GameGuideManager }     from './paytableSection/gameGuideManager/gameGuideManager.class'
import { PayoutManager }        from './paytableSection/payoutManager/payoutManager.class'
import { WinLinesWaysManager }  from './paytableSection/winLinesWaysManager/winLinesWaysManager.class'
import { Click }                from '../click/click.class'
import { ICONS }                from '../../constants/icons/icons.constant'
import { ONE_CLICK_HELP }       from '../../constants/oneClickHelp/oneClickHelp.constant'
import { getZIndex }            from '../../functions/get-zindex.function'

/**
 * @file Defines the {@link PaytableManager} class.
 * @author Joe Thompson <joe.thompson@1x2network.com>
 * @version 1.0.30
 */

 /** Class for handling new UI paytable */
 export class PaytableManager {

    /**
     * Reference to paytable config
     * @memberof PaytableManager
     * @protected
     * @instance
     * @type {Array.<object>}
     */
    _config

    /**
     * Object containing relevant configs for each paytable section
     * @memberof PaytableManager
     * @protected
     * @instance
     * @type {object}
     */
    _configs
    
    /**
     * Reference to paytable node
     * @memberof PaytableManager
     * @protected
     * @instance
     * @type {HTMLElement}
     */
    _elem

    /**
     * Reference to GameGuideManager
     * @memberof PaytableManager
     * @protected
     * @instance
     * @type {PaytableManager}
     */
    _gameGuideManager
    
    /**
     * Array of ID's for old ui paytable nodes that need to be hidden
     * @memberof PaytableManager
     * @protected
     * @instance
     * @type {string[]}
     */
    _oldPaytableIds

    /**
     * Initialize paytable structure within UI
     * @param {Promise<undefined>} loaded_ Promise for when buttonManager exposed to window
     */
    constructor(loaded_) {

        this._configs        = Object.create(null)                 //Create empty object to store relevant configs for each paytable section
        this._config         = window.paytableAssets.pages         //Store paytable config
        this._elem           = document.getElementById('paytable') //Initialize paytable node
        this._oldPaytableIds = [ 'pages', 'paytablePagination' ]   //Initialize old paytable node ID's

        //If Desktop
        if (!framework.isTouch()) {
            PAYTABLE_SECTIONS['exit'].icon = ICONS.exit.desktop //Load in desktop exit icon
        }

        //For each paytable section
        for (let i = 0; i < Object.keys(PAYTABLE_SECTIONS).length; i++) {

            let k            = Object.keys(PAYTABLE_SECTIONS)[i] //Store key for section
            this._configs[k] = []                                //Create empty array for storing config objects for each section

        }

        //Loop through config objects
        for (let i = 0; i < this._config.length; i++) {

            let obj = this._config[i] //Store current config

            //If game guide config
            if (obj.gameguide) {
                this._configs['gameGuide'].push(obj) //Add game guide config
            }

            //If WinLines config
            else if (obj.lines) {
                this._configs['winLinesWays'].push(obj) //Add WinLines/Ways config
            }

            //If WinWays config
            else if (obj.win_ways) {
                this._configs['winLinesWays'].push(obj) //Add WinLines/Ways config
            }

            //Payout config
            else {
                this._configs['payout'].push(obj) //Add payout config
            }

        }

        this.content(loaded_) //Generate paytable content

        //If mobile
        if (framework.isTouch()) {
            this.footer() //Generate paytable footer
        }

        //Desktop
        else {
            this.sidebar() //Generate paytable sidebar
        }

    }

    /** Get game guide manager */ get gameGuideManager() { return this._gameGuideManager }

    /** 
     * Builds DOM for paytable content 
     * @param {Promise<undefined>} loaded_ Promise for when buttonManager exposed to window
     */
    content(loaded_) {

        //Create div to store paytable contents
        let content = document.createElement('div')
        content.id  = 'paytable-content'
        content.classList.add('scrollable__content')

        //Loop through paytable sections
        for (let s in PAYTABLE_SECTIONS) {

            //Create div for each paytable section
            let page = document.createElement('div')
            let id   = PAYTABLE_SECTIONS[s].id
            page.id  = `${id}Page`
            page.classList.add('page')

            //If page isn't game guide
            if (id !== 'GameGuide') {
                page.classList.add('hidden') //Hide initially
            }

            //Switch id
            switch (id) {

                //Game guide
                case 'GameGuide':
                    this._gameGuideManager = new GameGuideManager(this._configs['gameGuide'], page, loaded_) //Initialize game guide manager
                    break
                
                //Payout
                case 'Payout':
                    new PayoutManager(this._configs['payout'], page) //Initialize payout manager
                    break
                
                //Win lines/ways
                case 'WinLinesWays':
                    new WinLinesWaysManager(this._configs['winLinesWays'], page) //Initialize win lines/ways manager
                    break

            }

            content.appendChild(page) //Add page to content

        }

        this._elem.appendChild(content) //Add content to paytable

    }

    /**
     * Generate icons to display in paytable
     * @return {HTMLUListElement} ul containing icons for each section
     */
    icons() {

        let ul = document.createElement('ul') //Create un ordered list

        //For each paytable section
        for (let s in PAYTABLE_SECTIONS) {

            //Load in section icon
            let li = document.createElement('li')

            //If section can be hidden based on some condition
            if (PAYTABLE_SECTIONS[s].hidden) {
                PAYTABLE_SECTIONS[s].hidden(s) && li.classList.add('hidden')    //Determine whether to hide section
            }

            //If mobile
            if (framework.isTouch()) {

                let span = document.createElement('span')  //Create span node for footer background
                span.classList.add('footer-bg')            //Add footer background styling
                span.innerHTML = PAYTABLE_SECTIONS[s].icon //Add paytable section icon

                li.appendChild(span) //Add span node to list item

            }

            //Desktop
            else {
                li.innerHTML = PAYTABLE_SECTIONS[s].icon //Load in sidebar icon
            }

            li.id = `${PAYTABLE_SECTIONS[s].id}Icon` //Set paytable section ID

            //If game guide
            if (PAYTABLE_SECTIONS[s].id === 'GameGuide') {
                li.classList.add('active') //Add active icon styling
            }

            //Add comment attribution
            let c = document.createComment('Credit: Font Awesome https://fontawesome.com/license')
            li.appendChild(c)

            //If mobile
            if (framework.isTouch()) {

                let pc = document.createElement('span') //Create span node
                pc.classList.add('pt-circle')           //Add styling to add circle effect in footer
                li.appendChild(pc)                      //Add to list item

            }

            PAYTABLE_SECTIONS[s].id !== 'Exit' ? new Click(li, this.click, this, li) : new Click(li, this.exit, this) //Add click handler

            ul.appendChild(li) //Add icon to list

        }

        return ul //Return unordered list with generated icons for each section

    }

    /** Builds DOM for paytable footer */
    footer() {

        //Create footer for storing paytable tab icons
        let footer = document.createElement('footer')
        footer.id  = 'paytable-footer'
        
        footer.appendChild(this.icons()) //Add list to footer

        this._elem.appendChild(footer) //Add footer to paytable node
        
    }

    /** Builds DOM for paytable sidebar */
    sidebar() {

        //Create sidebar
        let sidebar = document.createElement('div')
        sidebar.id = 'paytable-sidebar'

        sidebar.appendChild(this.icons()) //Add icons for each section to sidebar
        this._elem.appendChild(sidebar)   //Add sidebar to paytable node

    }

    /**
     * Click handler for switching between pages in paytable
     * @param {object} this_ - Reference to class's scope
     * @param {object} elem_ - Reference to page icon node
     */
    click(this_, elem_) {

        let content = document.getElementById('paytable-content') //Retrieve content node
        let pages   = content.children                            //Retrieve paytable section pages

        let currentPage //Variable for current page
        let newPage        //Variable for new page

        //Loop through pages
        for (let i = 0; i < pages.length; i++) {

            let page = pages[i] //Store page node

            //If page is showing
            if (!page.classList.contains('hidden')) {
                currentPage = document.getElementById(page.id) //Store current page
            }

        }

        //For each paytable section
        for (let s in PAYTABLE_SECTIONS) {

            let pageID    = `${PAYTABLE_SECTIONS[s].id}Page`  //Store page ID
            let clickedID = elem_.id.replace(/Icon/g, 'Page') //Store ID of page user wants to navigate to

            //If page is the one user wants to navigate to
            if (pageID === clickedID) {
                newPage = document.getElementById(clickedID) //Store page user wants to navigate to
            }

        }

        this_.fadePage(currentPage, newPage, content, elem_)    //Handle fading between pages

    }

    /**
     * Click handler for exiting paytable
     * @param {object} this_ - Reference to class's scope
     */
    exit(this_) {

        framework.hidePaytable()    //Close paytable

        //If Session bar in use
        if (window.f1x2.SESSION_BAR && window.f1x2.SESSION_BAR.use && !framework.isTouch()) {
            document.getElementById('mainViewport').style.zIndex = getZIndex('.gf-session-bar') + 1 //Display UI above session bar
        }

        //If help must be one click away
        if (ONE_CLICK_HELP.indexOf(loginVars.getRegParams().jurisdiction.toUpperCase()) > -1) {

            let content = document.getElementById('paytable-content') //Retrieve content node
            let pages   = content.children                            //Retrieve paytable section pages

            let currentPage //Variable for current page

            //Loop through pages
            for (let i = 0; i < pages.length; i++) {

                let page = pages[i] //Store page node

                //If page is showing
                if (!page.classList.contains('hidden')) {
                    currentPage = document.getElementById(page.id) //Store current page
                }

            }

            let newPage = document.getElementById('GameGuidePage')  //Store help page

            this_.fadePage(currentPage, newPage, content, null, 'GameGuideIcon')    //Handle fading between pages

        }

    }

    /**
     * Fade between pages when navigating paytable
     * @param {HTMLElement} current_ Current page we are viewing
     * @param {HTMLElement} new_ Page we are navigating to
     * @param {HTMLElement} content_ Reference to paytable content node
     * @param {HTMLElement} elem_ Page icon node
     * @param {string} icon_ Icon ID of page we want to navigate to
     */
    fadePage(current_, new_, content_, elem_, icon_) {

        //Fade out old page in via iron dog's fadeElement class
        fadeElement(current_, 'out', () => {

            //Fade new page in via iron dog's fadeElement class
            fadeElement(new_, 'in', () => {

                content_.scrollTop = 0 //Scroll to top of page

                let iconsParentID //Variable for storing icons parent ID

                //Get icons parent ID depending on device
                framework.isTouch() ? iconsParentID = 'paytable-footer' : iconsParentID = 'paytable-sidebar'

                //Store page icons
                let icons = document.getElementById(iconsParentID).querySelectorAll('ul')[0].children

                //Loop through icons
                for (let i = 0; i < icons.length; i++) {

                    let icon    = icons[i]  //Store current icon

                    //If icon matches icon of page we are navigating to
                    if (icon_ && icon.id === icon_) {
                        icon.classList.add('active')    //Add active styling
                    }

                    else {
                        icon.classList.remove('active') //Remove active styling
                    }

                }

                elem_ && elem_.classList.add('active') //Add active styling to current page

            })

        })

    }

 }